import React, { useState, useMemo, useCallback } from 'react';
import { Box, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import HandymanIcon from '@mui/icons-material/Handyman';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Permissions, usePermission } from '@billda/permissions-ui';
import { PageHeader } from '../../../shared/components/layout/PageHeader';
import { PageHeaderAction } from '../../../shared/components/layout/PageHeaderAction';
import { PageContentArea } from '../../../shared/components/layout/PageContentArea';
import { PageToolbar } from '../../../shared/components/layout/PageToolbar';
import ConfirmDialog from '../../../shared/components/ui/ConfirmDialog';
import { ResizableHandle, SidePanel } from '../../../shared/components/panel';
import { HelpPanel } from '../../../shared/components/help/HelpPanel';
import {
  LABOUR_ITEMS_HELP,
  LABOUR_GROUPS_HELP,
} from '../../../shared/constants/helpContent';
import { useAvailableLabourItems } from '../hooks/useAvailableLabourItems';
import { useLabourItemGroups } from '../hooks/useLabourItemGroups';
import { useDeleteLabourDefinition } from '../hooks/useLabourDefinitionMutations';
import { useDeleteGroupMutation } from '../hooks/useDeleteGroupMutation';
import { useLabourItemUIStore } from '../stores/labourItemUIStore';
import { useLabourGroupsPanelStore } from '../stores/labourGroupsPanelStore';
import { useGroupDetails } from '../hooks/useGroupDetails';
import ItemCreatePanel from '../components/ItemCreatePanel';
import GroupActionsMenu from '../components/GroupActionsMenu';
import GroupDetailsPanel from '../components/GroupDetailsPanel';
import GroupLifecyclePanel from '../components/GroupLifecyclePanel';
import { LabourItemsListView } from '../components/organisms/LabourItemsListView';
import { LabourGroupsListView } from '../components/organisms/LabourGroupsListView';
import { ChipTabs } from '../../../shared/components/ui/ChipTabs';

type CatalogueView = 'definitions' | 'groups';

const LabourCataloguePage = () => {
  // Permissions
  const canViewDefinitions = usePermission(Permissions.Labour.Edit);
  const canViewGroups = usePermission(Permissions.Labour.Groups.Manage);

  // Determine initial view and whether to show toggle
  const showToggle = canViewDefinitions && canViewGroups;
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const initialView: CatalogueView =
    tabParam === 'groups' && canViewGroups
      ? 'groups'
      : canViewDefinitions
        ? 'definitions'
        : 'groups';

  const [activeView, setActiveView] = useState<CatalogueView>(initialView);
  const [search, setSearch] = useState('');

  // Data fetching
  const { data: definitions = [], isLoading: definitionsLoading } =
    useAvailableLabourItems();
  const { data: groups = [], isLoading: groupsLoading } = useLabourItemGroups();

  // Definitions UI store (for panel state)
  const {
    isPanelOpen: isItemPanelOpen,
    panelContent: itemPanelContent,
    openItemCreatePanel,
    closePanel: closeItemPanel,
  } = useLabourItemUIStore();

  // Groups panel store
  const {
    isOpen: isGroupPanelOpen,
    contentType: groupContentType,
    groupId: selectedGroupId,
    mode: groupMode,
    openGroupDetails,
    openGroupCreate,
    closePanel: closeGroupPanel,
  } = useLabourGroupsPanelStore();

  // Group details (for panel)
  const { data: selectedGroup, isLoading: groupDetailLoading } =
    useGroupDetails(selectedGroupId);

  // Menu state (shared for both views)
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [menuItemId, setMenuItemId] = useState<string | null>(null);

  // Delete state
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState<string | null>(null);

  // Mutations
  const { mutate: deleteDefinition, isPending: deleteDefinitionPending } =
    useDeleteLabourDefinition();

  const { mutate: deleteGroup, isPending: deleteGroupPending } =
    useDeleteGroupMutation();

  const isLoading =
    activeView === 'definitions' ? definitionsLoading : groupsLoading;
  const isDeleting = deleteDefinitionPending || deleteGroupPending;
  const isPanelOpen = isItemPanelOpen || isGroupPanelOpen;

  // Filter items based on search
  const filteredDefinitions = useMemo(() => {
    if (!search.trim()) return definitions;
    const lower = search.toLowerCase();
    return definitions.filter(d => d.name.toLowerCase().includes(lower));
  }, [definitions, search]);

  const filteredGroups = useMemo(() => {
    if (!search.trim()) return groups;
    const lower = search.toLowerCase();
    return groups.filter(g => g.name.toLowerCase().includes(lower));
  }, [groups, search]);

  // Add button dropdown state
  const [addAnchorEl, setAddAnchorEl] = useState<HTMLElement | null>(null);
  const addMenuOpen = Boolean(addAnchorEl);

  // Handlers
  const handleTabChange = (newValue: string) => {
    setActiveView(newValue as CatalogueView);
    setSearch('');
  };

  const handleMenuOpen = useCallback(
    (event: React.MouseEvent<HTMLElement>, id: string) => {
      setMenuAnchorEl(event.currentTarget);
      setMenuItemId(id);
    },
    []
  );

  const handleMenuClose = useCallback(() => {
    setMenuAnchorEl(null);
    setMenuItemId(null);
  }, []);

  const handleDeleteClick = () => {
    if (menuItemId) {
      setDeleteItemId(menuItemId);
      setIsDeleteDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
    setDeleteItemId(null);
  };

  const handleConfirmDelete = () => {
    if (!deleteItemId) return;
    const callbacks = {
      onSettled: () => handleCloseDeleteDialog(),
    };
    if (activeView === 'definitions') {
      deleteDefinition(deleteItemId, callbacks);
    } else {
      deleteGroup(deleteItemId, callbacks);
    }
  };

  const handleAddClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    // If user can only add one type, go directly
    if (canViewDefinitions && !canViewGroups) {
      openItemCreatePanel();
      return;
    }
    if (!canViewDefinitions && canViewGroups) {
      openGroupCreate();
      return;
    }
    setAddAnchorEl(event.currentTarget);
  };

  const handleAddMenuClose = () => {
    setAddAnchorEl(null);
  };

  const handleAddItem = () => {
    handleAddMenuClose();
    openItemCreatePanel();
  };

  const handleAddGroup = () => {
    handleAddMenuClose();
    openGroupCreate();
  };

  const handleClosePanel = () => {
    if (isItemPanelOpen) closeItemPanel();
    if (isGroupPanelOpen) closeGroupPanel();
  };

  const getPanelTitle = () => {
    // Item panels
    if (isItemPanelOpen) {
      if (itemPanelContent === 'help') return 'Help';
      return 'Add Labour Item';
    }
    // Group panels
    if (isGroupPanelOpen) {
      if (groupContentType === 'group-details')
        return selectedGroup?.name || 'Group Details';
      if (groupContentType === 'group-lifecycle')
        return groupMode === 'create' ? 'Create Group' : 'Edit Group';
      if (groupContentType === 'help') return 'Help';
    }
    return 'Details';
  };

  const getPanelTestId = () => {
    if (isItemPanelOpen) {
      if (itemPanelContent === 'item-create') return 'labour-item-create-panel';
      if (itemPanelContent === 'help') return 'labour-items-help-drawer';
    }
    if (isGroupPanelOpen) {
      if (groupContentType === 'group-details')
        return 'labour-group-details-panel';
      if (groupContentType === 'group-lifecycle')
        return 'labour-group-lifecycle-panel';
      if (groupContentType === 'help') return 'labour-groups-help-drawer';
    }
    return undefined;
  };

  return (
    <PageContentArea
      isPanelOpen={isPanelOpen}
      panel={
        isPanelOpen ? (
          <>
            <ResizableHandle />
            <SidePanel
              open={isPanelOpen}
              onClose={handleClosePanel}
              title={getPanelTitle()}
              testId={getPanelTestId()}
            >
              {/* Item panels */}
              {isItemPanelOpen && itemPanelContent === 'item-create' && (
                <ItemCreatePanel />
              )}
              {isItemPanelOpen && itemPanelContent === 'help' && (
                <HelpPanel helpContent={LABOUR_ITEMS_HELP} />
              )}
              {/* Group panels */}
              {isGroupPanelOpen && groupContentType === 'group-details' && (
                <GroupDetailsPanel
                  group={selectedGroup ?? null}
                  loading={groupDetailLoading}
                />
              )}
              {isGroupPanelOpen && groupContentType === 'group-lifecycle' && (
                <GroupLifecyclePanel group={selectedGroup ?? null} />
              )}
              {isGroupPanelOpen && groupContentType === 'help' && (
                <HelpPanel helpContent={LABOUR_GROUPS_HELP} />
              )}
            </SidePanel>
          </>
        ) : undefined
      }
    >
      <PageHeader
        title="Labour"
        actions={
          <>
            <PageHeaderAction
              label="Add"
              onClick={handleAddClick}
              icon={<AddIcon />}
              endIcon={
                canViewDefinitions && canViewGroups ? (
                  <KeyboardArrowDownIcon />
                ) : undefined
              }
              disabled={isLoading || isDeleting}
              data-testid="add-labour-button"
              aria-controls={addMenuOpen ? 'add-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={addMenuOpen ? 'true' : undefined}
            />
            <Menu
              id="add-menu"
              anchorEl={addAnchorEl}
              open={addMenuOpen}
              onClose={handleAddMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <MenuItem
                onClick={handleAddItem}
                data-testid="add-item-menu-option"
              >
                <ListItemIcon>
                  <HandymanIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Item</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={handleAddGroup}
                data-testid="add-group-menu-option"
              >
                <ListItemIcon>
                  <WorkspacesIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Group</ListItemText>
              </MenuItem>
            </Menu>
          </>
        }
        data-testid="labour-catalogue-header"
      />

      <PageToolbar
        tabs={
          showToggle ? (
            <ChipTabs
              tabs={[
                { label: 'Definitions', value: 'definitions' },
                { label: 'Groups', value: 'groups' },
              ]}
              value={activeView}
              onChange={handleTabChange}
              data-testid="labour-catalogue-tabs"
            />
          ) : undefined
        }
        searchValue={search}
        onSearchChange={setSearch}
      />

      {/* List View */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
        }}
      >
        {activeView === 'definitions' ? (
          <LabourItemsListView
            items={filteredDefinitions}
            loading={definitionsLoading}
            onMenuOpen={handleMenuOpen}
            disabled={isDeleting}
          />
        ) : (
          <LabourGroupsListView
            groups={filteredGroups}
            loading={groupsLoading}
            onItemClick={openGroupDetails}
            onMenuOpen={handleMenuOpen}
            disabled={isDeleting}
            onAddGroup={openGroupCreate}
          />
        )}
      </Box>

      {/* Actions Menu */}
      <GroupActionsMenu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        onDelete={handleDeleteClick}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={isDeleteDialogOpen}
        title={
          activeView === 'definitions'
            ? 'Delete Labour Item'
            : 'Delete Labour Item Group'
        }
        message={
          activeView === 'definitions'
            ? 'Are you sure you want to delete this labour item?'
            : 'Are you sure you want to delete this labour item group? This action cannot be undone.'
        }
        onCancel={handleCloseDeleteDialog}
        onConfirm={handleConfirmDelete}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </PageContentArea>
  );
};

export default LabourCataloguePage;
